import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandBooking,
  BrandLiveSession,
  acceptBrandBooking,
  declineBrandBooking,
  listBrandLiveSessions,
  listCreatorBrandBookings,
  setBrandLiveSessionLink,
} from '../../api/payment/brand-live.api';
import { hoursLabel, liveStopReasonLabel, validateLiveUrl } from '../../utils/brand-live';
import DialogComponent from '../dialog/dialog.component';
import BrandLiveCancelDialog from './brand-live-cancel-dialog';
import BrandLiveCard from './brand-live-card';
import BrandLiveReportList from './brand-live-report-list';
import { IndividualOrdererBadge } from '../brand-deal/orderer-badge';
import { useBrandTaxConsent } from '../../hooks/use-brand-tax-consent';
import { BrandThreadToggle } from '../brand-deal/brand-thread-panel';

const PAID_CONTENT_REMINDER =
  'Khi chạy quảng cáo này, hãy bật mục khai báo nội dung có trả tiền trên YouTube, TikTok hoặc Facebook.';

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const formatVnd = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '');
const goUrl = (code: string) => `${window.location.origin}/go/${code}`;

const isPending = (b: BrandBooking) =>
  b.live?.status === 'Invited' || b.post?.status === 'Invited';
const isRunning = (b: BrandBooking) => b.live?.status === 'Airing';

/** 'incoming' chỉ hiện đơn chờ nhận (mục "Yêu cầu nhận được"); 'active' hiện phần đang chạy, buổi live và báo
 * cáo gian lận (mục "Quảng cáo trên livestream"), kèm dòng nhắc chuyển sang đơn chờ nhận nếu có; 'all' hiện đủ. */
type BrandLiveCreatorView = 'all' | 'incoming' | 'active';

const BrandLiveCreatorTab: React.FC<{
  onChanged?: () => void;
  view?: BrandLiveCreatorView;
  onGoToIncoming?: () => void;
}> = ({ onChanged, view = 'all', onGoToIncoming }) => {
  const showIncoming = view !== 'active';
  const showActive = view !== 'incoming';
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<BrandBooking[]>([]);
  const [sessions, setSessions] = useState<BrandLiveSession[]>([]);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [declineTarget, setDeclineTarget] = useState<BrandBooking | null>(null);
  const [cancelTarget, setCancelTarget] = useState<BrandBooking | null>(null);
  const [reportsKey, setReportsKey] = useState(0);
  const taxConsent = useBrandTaxConsent();

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([listCreatorBrandBookings(), listBrandLiveSessions()]);
      setBookings(b);
      setSessions(s);
    } catch (err) {
      toast.error(errorMessage(err, 'Không tải được danh sách quảng cáo livestream.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const accept = async (b: BrandBooking) => {
    if (busyId !== null) return;
    setBusyId(b.campaignId);
    try {
      const res = await acceptBrandBooking(b.campaignId);
      toast.success('Đã nhận đơn đặt quảng cáo.');
      toast(res.notice || PAID_CONTENT_REMINDER, { icon: 'ℹ️' });
      if (res.message) toast(res.message, { icon: '⚠️', duration: 8000 });
      await load();
      onChanged?.();
    } catch (err) {
      if (taxConsent.handle(err, () => accept(b))) return;
      toast.error(errorMessage(err, 'Không nhận được đơn.'));
    } finally {
      setBusyId(null);
    }
  };

  const decline = async () => {
    if (!declineTarget || busyId !== null) return;
    setBusyId(declineTarget.campaignId);
    try {
      await declineBrandBooking(declineTarget.campaignId);
      toast.success('Đã từ chối đơn.');
      setDeclineTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, 'Không từ chối được đơn.'));
      setDeclineTarget(null);
    } finally {
      setBusyId(null);
    }
  };

  const saveLink = async (s: BrandLiveSession) => {
    const url = (links[s.id] ?? '').trim();
    const invalid = validateLiveUrl(url);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setBusyId(s.id);
    try {
      const updated = await setBrandLiveSessionLink(s.id, url);
      setSessions((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
      toast.success('Đã lưu link buổi live.');
    } catch (err) {
      toast.error(errorMessage(err, 'Không lưu được link.'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }

  const pending = bookings.filter(isPending);
  const running = bookings.filter(isRunning);

  return (
    <div className="flex flex-col gap-4">
      {showActive && !showIncoming && pending.length > 0 && onGoToIncoming && (
        <button
          type="button"
          onClick={onGoToIncoming}
          className="flex items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-left text-[14px] font-semibold text-[#0057FF] dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300"
        >
          <span>Có {pending.length} đơn quảng cáo livestream đang chờ bạn nhận.</span>
          <span className="shrink-0 rounded-full border border-current px-2.5 py-1 text-[12px]">Xem đơn</span>
        </button>
      )}

      {showIncoming && (
        <section className="flex flex-col gap-3">
          <div className="ui-heading text-[15px] font-bold">Đơn chờ bạn nhận</div>
          {pending.length === 0 && <p className="ui-muted text-[14px]">Chưa có đơn nào đang chờ.</p>}
          {pending.map((b) => (
            <div key={b.campaignId} className="ui-card flex flex-col gap-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="ui-heading flex items-center gap-2 text-[15px] font-bold">
                  {b.businessName}
                  <IndividualOrdererBadge kind={b.ordererKind} />
                </div>
                <div className="ui-heading text-[15px] font-bold">{formatVnd(b.totalVnd)}</div>
              </div>
              {b.respondBy && <div className="ui-muted text-[13px]">Trả lời trước {fmtTime(b.respondBy)}</div>}
              <div className="flex flex-wrap gap-4">
                {b.live && (
                  <div className="flex flex-col gap-2">
                    <div className="ui-text text-[13px]">
                      Livestream: {b.live.plan === 'Monthly' ? 'gói tháng' : 'mua theo giờ'}, {hoursLabel(b.live.purchasedSeconds)}, hiệu lực {b.live.validDays} ngày, tại vị trí {b.live.slotName}, {formatVnd(b.live.priceVnd)}
                    </div>
                    <div className="ui-surface-2 flex justify-center rounded-xl p-3">
                      <BrandLiveCard
                        preview
                        shape={b.live.slotShape}
                        ad={{ logoUrl: b.live.cardLogo.url, title: b.live.cardTitle, line: b.live.cardLine, goUrl: goUrl(b.live.shareCode) }}
                      />
                    </div>
                  </div>
                )}
                {b.post && (
                  <div className="ui-surface-2 flex min-w-[220px] flex-1 flex-col gap-2 rounded-xl p-3">
                    <div className="ui-text text-[13px] font-semibold">Phần bài đăng, {formatVnd(b.post.priceVnd)}</div>
                    <p className="ui-text whitespace-pre-line text-[13px]">{b.post.caption}</p>
                    {b.post.images.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {b.post.images.map((m) => (
                          <img key={m.id} src={m.url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                        ))}
                      </div>
                    )}
                    {b.post.videos.length > 0 && <div className="ui-muted text-[12px]">Kèm {b.post.videos.length} video</div>}
                  </div>
                )}
              </div>
              <p className="ui-muted text-[13px]">{PAID_CONTENT_REMINDER}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => accept(b)}
                  className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  Nhận cả đơn
                </button>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => setDeclineTarget(b)}
                  className="rounded-lg border border-red-500 px-4 py-2 text-[14px] font-semibold text-red-500 disabled:opacity-60"
                >
                  Từ chối cả đơn
                </button>
              </div>
              <BrandThreadToggle threadId={b.threadId} />
            </div>
          ))}
        </section>

      )}

      {showActive && (
        <>
          <section className="flex flex-col gap-3">
            <div className="ui-heading text-[15px] font-bold">Đang chạy</div>
            {running.length === 0 && <p className="ui-muted text-[14px]">Chưa có quảng cáo livestream nào đang chạy.</p>}
            {running.map((b) => {
              const l = b.live!;
              const pct = l.purchasedSeconds > 0 ? Math.min(100, Math.round((l.shownSeconds / l.purchasedSeconds) * 100)) : 0;
              return (
                <div key={b.campaignId} className="ui-card flex flex-col gap-2 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="ui-heading flex items-center gap-2 text-[15px] font-bold">
                      {b.businessName}
                      <IndividualOrdererBadge kind={b.ordererKind} />
                    </div>
                    <span className="ui-muted text-[13px]">{l.slotName}</span>
                  </div>
                  <div className="ui-text text-[13px]">
                    Đã chạy {hoursLabel(l.shownSeconds)} trên {hoursLabel(l.purchasedSeconds)} đã mua
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                    <div className="h-full bg-[#0057FF]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="ui-muted text-[13px]">
                    {l.endsAt ? `Hết hạn ${fmtTime(l.endsAt)} · ` : ''}Đã nhận {formatVnd(l.paidGrossVnd - l.paidFeeVnd)} sau phí
                  </div>
                  {l.stoppedAt ? (
                    <div className="text-[13px] text-amber-500">
                      {liveStopReasonLabel(l.stopReason) || 'Đã dừng'}
                      {l.pendingCancel ? '. Đang chờ admin xét yêu cầu của bạn.' : '.'}
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setCancelTarget(b)}
                        className="rounded-lg border border-red-500 px-3 py-1.5 text-[13px] font-semibold text-red-500"
                      >
                        Huỷ hợp tác
                      </button>
                    </div>
                  )}
                  <BrandThreadToggle threadId={b.threadId} />
                </div>
              );
            })}
          </section>

          <section className="flex flex-col gap-3">
            <div className="ui-heading text-[15px] font-bold">Buổi live của bạn</div>
            {sessions.length === 0 && <p className="ui-muted text-[14px]">Chưa có buổi live nào có quảng cáo chạy.</p>}
            {sessions.map((s) => (
              <div key={s.id} className="ui-card flex flex-col gap-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 text-[14px]">
                  <span className="ui-heading font-semibold">{fmtTime(s.startedAt)}</span>
                  <span className="ui-muted text-[13px]">Được tính {Math.round(s.countedSeconds / 60)} phút</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className="ui-input min-w-[200px] flex-1 px-3 py-2 text-[13px]"
                    placeholder="Dán link buổi live (YouTube, TikTok hoặc Facebook)"
                    value={links[s.id] ?? s.liveUrl ?? ''}
                    onChange={(e) => setLinks((p) => ({ ...p, [s.id]: e.target.value }))}
                  />
                  <button
                    type="button"
                    disabled={busyId === s.id}
                    onClick={() => saveLink(s)}
                    className="rounded-lg bg-[#0057FF] px-3 py-2 text-[13px] font-semibold text-white disabled:opacity-60"
                  >
                    Lưu link
                  </button>
                </div>
                {!s.liveUrl && s.linkDueAt && (
                  <div className={`text-[13px] ${s.linkMissing ? 'text-red-500' : 'ui-muted'}`}>
                    {s.linkMissing ? 'Đã quá hạn dán link, buổi này không được tính.' : `Cần dán link trước ${fmtTime(s.linkDueAt)}`}
                  </div>
                )}
              </div>
            ))}
          </section>

          <section className="flex flex-col gap-3">
            <div className="ui-heading text-[15px] font-bold">Báo cáo gian lận</div>
            <BrandLiveReportList role="creator" refreshKey={reportsKey} />
          </section>
        </>
      )}

      {cancelTarget?.live && (
        <BrandLiveCancelDialog
          dealId={cancelTarget.live.dealId}
          businessName={cancelTarget.businessName}
          onClose={() => setCancelTarget(null)}
          onDone={() => {
            setCancelTarget(null);
            setReportsKey((k) => k + 1);
            load();
            onChanged?.();
          }}
        />
      )}

      <DialogComponent visible={!!declineTarget} title="Từ chối đơn" onClose={() => busyId === null && setDeclineTarget(null)}>
        <div className="flex flex-col gap-3 p-4">
          <p className="ui-text text-[14px]">Từ chối cả đơn của {declineTarget?.businessName}? Tiền sẽ hoàn lại cho doanh nghiệp.</p>
          <div className="flex justify-end gap-2">
            <button type="button" disabled={busyId !== null} onClick={() => setDeclineTarget(null)} className="ui-text rounded-lg border ui-divider px-4 py-2 text-[14px]">
              Huỷ
            </button>
            <button type="button" disabled={busyId !== null} onClick={decline} className="rounded-lg bg-red-600 px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60">
              Từ chối
            </button>
          </div>
        </div>
      </DialogComponent>
      {taxConsent.dialog}
    </div>
  );
};

export default BrandLiveCreatorTab;
